import { useMemo } from "react";

import type { LineSchema, ThermometerResponse, ThermometerVehicle } from "../contract/types";
import { useBackend } from "../app/BackendProvider";
import { describeError } from "../app/useResource";
import { formatDeviation, shortVehicleNumber } from "../app/format";
import { useNetworkId } from "../config/ConfigProvider";
import { useLines } from "../config/LinesProvider";
import { usePolledResource } from "../cockpit/hooks";

/**
 * Image ligne « schéma des voies » (P2-29, contrat v1.24/v1.25). Quand une ligne
 * a un `config.lineSchemas[]`, sa vue thermomètre **est** ce schéma (réplique
 * fidèle de la maquette) plutôt que les deux rails génériques. Les véhicules
 * sont placés **exactement** : `thermometer.vehicles[].stopIndex` le long de la
 * branche (`branchId`), interpolé entre les `nodes` du schéma. Le SVG est servi
 * par l'API (`assetUrl`, D-013).
 */
export function MetroSchema({ lineId, schema }: { lineId: string; schema: LineSchema }) {
  const backend = useBackend();
  const networkId = useNetworkId();
  const lines = useLines();
  const lineName = lines.byId(lineId)?.name ?? lines.code(lineId);

  const { data, error, loading } = usePolledResource(
    (signal) => backend.api.getThermometer(networkId, { lineId }, signal),
    [backend, networkId, lineId],
    12_000,
  );

  // `assetUrl` est un chemin depuis la racine de l'API (D-013). En cross-origin
  // (`VITE_API_BASE_URL` défini) on le préfixe. En mocks, la valeur pointe déjà
  // un asset bundlé servi par le même hôte : rien à préfixer.
  const apiBase = import.meta.env.VITE_API_BASE_URL ?? "";
  const assetUrl =
    backend.usesMocks || schema.assetUrl.startsWith("http") ? schema.assetUrl : `${apiBase}${schema.assetUrl}`;
  const vehicles = data?.vehicles ?? [];
  const terminusColor = useMemo(() => terminusColors(data?.directions ?? [], vehicles), [data, vehicles]);

  const placed = useMemo(
    () => (data ? vehicles.map((v) => ({ v, at: placeVehicle(v, data, schema) })).filter((p) => p.at !== null) : []),
    [data, vehicles, schema],
  );

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 10, flexWrap: "wrap" }}>
        <h2 className="ovl">Schéma des voies — {lineName}</h2>
        <span className="mono" style={{ fontSize: "0.7rem", color: "var(--sentinelle-muted)" }}>
          {vehicles.length} véhicule{vehicles.length > 1 ? "s" : ""}
        </span>
      </div>

      {loading && !data ? <p className="has-text-grey is-size-7">Chargement…</p> : null}
      {error && !data ? <p className="has-text-danger is-size-7">{describeError(error)}</p> : null}

      {!assetUrl ? (
        <p className="has-text-grey is-size-7">Schéma de voies indisponible pour cette ligne.</p>
      ) : (
        <div className="mae-metro-schema" style={{ position: "relative", width: "100%", overflowX: "auto" }}>
          <img
            src={assetUrl}
            alt="Schéma des voies du métro : double voie V1/V2, exploitation en Y à Saint-Sever, embranchements dépôt."
            style={{ display: "block", width: "100%", minWidth: 960 }}
          />
          <svg
            viewBox={schema.viewBox}
            preserveAspectRatio="xMinYMin meet"
            style={{ position: "absolute", inset: 0, width: "100%", height: "auto", minWidth: 960, pointerEvents: "none" }}
            aria-hidden
          >
            {placed.map(({ v, at }) => (
              <MetroMarker
                key={v.journeyId}
                v={v}
                x={at!.x}
                y={at!.y}
                originColor={terminusColor(v.originHeadsign)}
                destColor={terminusColor(v.destinationHeadsign)}
              />
            ))}
          </svg>
        </div>
      )}

      <div style={{ display: "flex", gap: 16, marginTop: 10, fontSize: "0.68rem", color: "var(--sentinelle-muted)", flexWrap: "wrap" }}>
        <Sw c="#86efac" l="retard absorbé / à l'heure" />
        <Sw c="#15803d" l="repartira en retard" />
        <Sw c="#dc2626" l="en avance" />
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", border: "1px solid var(--sentinelle-ink-3)" }} />
          dernière course (retour dépôt)
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, border: "2px solid #000", animation: "mae-blink 1s steps(1) infinite" }} />
          relève prévue
        </span>
      </div>
    </div>
  );
}

/**
 * Position exacte d'un véhicule : sa branche (`branchId`, sinon la 1ʳᵉ du sens),
 * puis interpolation entre les `nodes` du schéma aux indices encadrant
 * `stopIndex`. `null` si la branche ou les nœuds manquent.
 */
function placeVehicle(
  v: ThermometerVehicle,
  data: ThermometerResponse,
  schema: LineSchema,
): { x: number; y: number } | null {
  const dir = data.directions.find((d) => d.direction === v.direction);
  if (!dir || dir.branches.length === 0) return null;
  const branch = dir.branches.find((b) => b.branchId === v.branchId) ?? dir.branches[0]!;
  const ids = branch.stopIds;
  if (ids.length === 0) return null;

  const t = Math.max(0, Math.min(ids.length - 1, v.stopIndex));
  const i = Math.floor(t);
  const a = schema.nodes[ids[i]!];
  const b = schema.nodes[ids[Math.min(i + 1, ids.length - 1)]!];
  if (!a || !b) return null;
  const f = t - i;
  return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
}

function MetroMarker({
  v,
  x,
  y,
  originColor,
  destColor,
}: {
  v: ThermometerVehicle;
  x: number;
  y: number;
  originColor: string;
  destColor: string;
}) {
  const bg = squareFill(v);
  const s = 30;
  return (
    <g transform={`translate(${x} ${y})`}>
      <title>
        {shortVehicleNumber(v.vehicleDisplayNumber)} · écart {formatDeviation(v.deviationSeconds)}
        {v.releve ? " · relève" : ""}
        {v.endOfService ? " · retour dépôt" : ""}
      </title>
      {v.releve ? (
        <rect x={-s / 2 - 6} y={-s / 2 - 6} width={s + 12} height={s + 12} rx={4} fill="none" stroke="#000" strokeWidth={4} className="mae-metro-blink" />
      ) : null}
      <rect x={-s / 2} y={-s / 2} width={s} height={s} rx={4} fill={bg} stroke="#fff" strokeWidth={2} />
      <rect x={-s / 2} y={-s / 2} width={5} height={s} fill={originColor} />
      <rect x={s / 2 - 5} y={-s / 2} width={5} height={s} fill={destColor} />
      {v.endOfService ? <circle cx={0} cy={0} r={5} fill="#fff" stroke="#0006" /> : null}
      <text x={0} y={s / 2 + 16} textAnchor="middle" fontSize={16} fontFamily="var(--bulma-family-code)" fill="var(--sentinelle-ink)">
        {shortVehicleNumber(v.vehicleDisplayNumber)}
      </text>
    </g>
  );
}

/** Fond du carré (P2-24) — cohérent avec le thermomètre. */
function squareFill(v: ThermometerVehicle): string {
  if (v.deviationSeconds < 0) return "#dc2626"; // avance, §E.3
  if (v.deviationSeconds >= 60) {
    return v.nextBattementSeconds != null && v.deviationSeconds <= v.nextBattementSeconds ? "#86efac" : "#15803d";
  }
  return "#86efac";
}

function terminusColors(
  directions: { stops: { name: string }[] }[],
  vehicles: ThermometerVehicle[],
): (name: string) => string {
  const names = new Set<string>();
  for (const d of directions) {
    if (d.stops[0]) names.add(d.stops[0].name);
    const last = d.stops.at(-1);
    if (last) names.add(last.name);
  }
  for (const v of vehicles) {
    names.add(v.originHeadsign);
    names.add(v.destinationHeadsign);
  }
  const palette = ["#2b6cb0", "#c026d3", "#0d9488", "#ca8a04", "#dc2626", "#4f46e5", "#65a30d", "#db2777"];
  const map = new Map<string, string>();
  [...names]
    .filter(Boolean)
    .sort()
    .forEach((n, i) => map.set(n, palette[i % palette.length]!));
  return (name: string) => map.get(name) ?? "#64748b";
}

function Sw({ c, l }: { c: string; l: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span style={{ width: 12, height: 12, borderRadius: 2, background: c }} aria-hidden />
      {l}
    </span>
  );
}
